import { z } from "zod";
import { ApiCallError } from "@/lib/api-call-error";
import { reviveDates } from "@/lib/revive-dates";

// Parse (not cast) the `{ ok: false, error }` envelope body out of an unknown
// error payload, so reading `code`/`message`/`data` needs no type assertion.
const EnvelopeErrorSchema = z.object({
  error: z
    .object({
      code: z.string().optional(),
      message: z.string().optional(),
      data: z.record(z.string(), z.unknown()).optional(),
    })
    .optional(),
});

/**
 * Shape of an Eden Treaty call result, narrowed to what we read. On a 2xx the
 * success `data` is the backend envelope `{ ok: true, data: T }`. On a transport
 * error `error.value` carries the `{ ok: false, error }` envelope body. The
 * `{ ok: false }` arm of `data` is defensive only — this backend always uses
 * HTTP status codes for errors — but it keeps `edenData`'s 200-with-ok:false
 * guard representable.
 */
export interface EdenResult<T> {
  data: { ok: true; data: T } | { ok: false; error?: { code?: string; message?: string } } | null;
  error: { status: number; value: unknown } | null;
  // Eden's TreatyResponse always populates the HTTP status; required here so the
  // error paths report the real status rather than a misleading fallback.
  status: number;
}

function toApiError(status: number, value: unknown): ApiCallError {
  const error = EnvelopeErrorSchema.safeParse(value).data?.error;
  const code = error?.code ?? "API_ERROR";
  const message = error?.message ?? `Request failed (HTTP ${status})`;
  return new ApiCallError(status, code, message, error?.data);
}

/**
 * Unwrap an Eden Treaty result to the inner envelope `data`, or throw a
 * normalized ApiCallError so the existing error-registry / notify pipeline
 * keeps working unchanged. Handles Eden transport errors AND 200-with-ok:false.
 */
export function edenData<T>(res: EdenResult<T>): T {
  if (res.error) throw toApiError(res.error.status, res.error.value);
  if (res.data == null) throw new ApiCallError(res.status, "API_ERROR", "Empty response");
  if (res.data.ok === false) throw toApiError(res.status, res.data);
  // Revive ISO datetime strings to Date so the runtime value matches Eden's
  // `Date`-typed fields (the wire carries strings; see revive-dates.ts). This
  // is the single unavoidable narrowing in the Eden seam: a generic deep clone
  // that swaps string↔Date at date-named keys provably yields the same `T`
  // (whose date fields are already typed `Date`), but TypeScript cannot express
  // that a structural recursion over `unknown` preserves `T`.
  return reviveDates(res.data.data) as T;
}
